"use client";

import { useEffect, useState } from "react";
import { usePrivy } from "@privy-io/react-auth";

import PortalNavBar from "../components/PortalNavBar";
import { backendFetch } from "../lib/backend";

type Sale = {
  id: number;
  transferId: string;
  agreementAddress: string;
  agreementRole?: string;
  expectedAmount: number;
  status: "awaiting_settlement" | "platform_deposited" | "deposited" | "failed" | "refunded" | "disputed" | "refund_review";
  createdAt: string;
};

const STATUS: Record<Sale["status"], { text: string; className: string }> = {
  awaiting_settlement: { text: "Awaiting settlement", className: "text-amber-300" },
  platform_deposited: { text: "Settling", className: "text-blue-300" },
  deposited: { text: "Paid out", className: "text-green-400" },
  failed: { text: "Delayed - under review", className: "text-red-400" },
  refunded: { text: "Refunded", className: "text-slate-400" },
  disputed: { text: "Disputed", className: "text-red-400" },
  refund_review: { text: "Under review", className: "text-red-400" },
};

const money = (value: number) => `$${value.toFixed(2)}`;
const shortAddress = (value: string) => `${value.slice(0, 6)}...${value.slice(-4)}`;

// Role dashboards each have their own nav; this shared page keeps a neutral set of links.
const navLinks = [
  { href: "/dashboard", label: "Dashboard" },
  { href: "/account", label: "Account" },
  { href: "/sales-history", label: "Sales History", active: true },
];

export const dynamic = "force-dynamic";

export default function SalesHistoryPage() {
  const { ready, authenticated, login, getAccessToken } = usePrivy();
  const [sales, setSales] = useState<Sale[] | null>(null);
  const [scope, setScope] = useState<"revenue_distributor" | "agreement">("agreement");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!ready || !authenticated) return;
    let active = true;

    async function load() {
      try {
        const token = await getAccessToken();
        if (!token) throw new Error("Authentication token unavailable.");
        const response = await backendFetch("/api/pos/transactions", token);
        const payload = await response.json();
        if (!response.ok || !payload.success) throw new Error(payload.error || "Unable to load sales.");
        if (active) {
          setScope(payload.scope);
          setSales(payload.transactions);
        }
      } catch (loadError) {
        if (active) setError(loadError instanceof Error ? loadError.message : "Unable to load sales.");
      }
    }

    void load();
    return () => {
      active = false;
    };
  }, [authenticated, getAccessToken, ready]);

  if (!ready || !authenticated) {
    return (
      <main className="min-h-screen bg-slate-950 p-10 text-white">
        <button type="button" onClick={login} className="rounded-xl bg-blue-600 px-5 py-3 font-semibold">
          Sign in to view sales history
        </button>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-slate-950 px-6 py-10 text-white">
      <div className="mx-auto max-w-5xl">
        <PortalNavBar links={navLinks} portalTitle="Sales History" />
        <h1 className="text-3xl font-bold">Sales History</h1>
        <p className="mt-2 text-sm text-slate-400">
          {scope === "revenue_distributor"
            ? "Card sales across every agreement, showing the fee the RevenueDistributor will receive."
            : "Card sales for your agreement, showing what it will receive after card processing and platform fees."}
          {" "}This is a preview. Revenue appears in your revenue history only after a sale settles.
        </p>

        {error && <p role="alert" className="mt-6 text-sm text-red-300">{error}</p>}
        {!sales && !error && <p className="mt-6 text-sm text-slate-300">Loading sales...</p>}
        {sales && sales.length === 0 && (
          <p className="mt-6 text-sm text-slate-400">No sales yet. Completed card sales appear here.</p>
        )}

        {sales && sales.length > 0 && (
          <div className="mt-6 overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-slate-800 text-xs uppercase text-slate-400">
                <tr>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Agreement</th>
                  {scope === "agreement" && <th className="px-4 py-3">Your role</th>}
                  <th className="px-4 py-3 text-right">
                    {scope === "revenue_distributor" ? "RevenueDistributor fee" : "Expected to agreement"}
                  </th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {sales.map((sale) => {
                  const status = STATUS[sale.status] ?? { text: sale.status, className: "text-slate-300" };
                  return (
                    <tr key={sale.id} className="border-b border-slate-800 last:border-0">
                      <td className="px-4 py-3 text-slate-300">{new Date(sale.createdAt).toLocaleString()}</td>
                      <td className="px-4 py-3 font-mono text-slate-200">{shortAddress(sale.agreementAddress)}</td>
                      {scope === "agreement" && (
                        <td className="px-4 py-3 text-slate-300">{sale.agreementRole ?? "-"}</td>
                      )}
                      <td className="px-4 py-3 text-right font-semibold">{money(sale.expectedAmount)}</td>
                      <td className={`px-4 py-3 font-medium ${status.className}`}>{status.text}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </main>
  );
}
